import axios     from "axios";
import { API } from "./apiConfig";

export const AUTH_LOGOUT_EVENT = "auth:logout";
export const AUTH_TOKEN_EVENT = "auth:token";
export const SESSION_EXPIRED_MESSAGE =
  "Your session has expired. Please log in again.";

const AUTH_USER_KEY = "auth_user";
const AUTH_EXPIRES_KEY = "auth_session_expires_at";
const LEGACY_TOKEN_KEYS = ["token", "jwt", "authToken"];

/* ─────────────────────────────────────────────
   Auth helpers (register, availability checks)
   ───────────────────────────────────────────── */

export const register = async (username, email, password, extra = {}) => {
  const payload = { username, email, password, ...extra };
  const { data } = await axios.post(`${API}/register`, payload);
  return data;
};

export const checkUsernameAvailability = async (username) => {
  const { data } = await axios.get(`${API}/check-username`, { params: { username } });
  return data;
};

export const checkEmailAvailability = async (email) => {
  const { data } = await axios.get(`${API}/check-email`, { params: { email } });
  return data;
};

/* ─────────────────────────────────────────────
   Login & token helpers
   ───────────────────────────────────────────── */

const storeAuthenticatedSession = (data) => {
  LEGACY_TOKEN_KEYS.forEach((key) => localStorage.removeItem(key));
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(data.user));
  if (data.session?.expiresAt) {
    localStorage.setItem(AUTH_EXPIRES_KEY, data.session.expiresAt);
  }
  localStorage.setItem("user_id", data.user.id);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(AUTH_TOKEN_EVENT));
  }
};

export const login = async (username, password) => {
  const { data } = await axios.post(`${API}/login`, { username, password }, { withCredentials: true });
  if (data.mfaRequired) return data;
  if (!data.user) throw new Error("No authenticated user returned");
  storeAuthenticatedSession(data);
  return data;
};

export const verifyMfaLogin = async (mfaToken, code) => {
  const { data } = await axios.post(`${API}/login/mfa`, { mfaToken, code }, { withCredentials: true });
  if (!data.user) throw new Error("No authenticated user returned");
  storeAuthenticatedSession(data);
  return data;
};

export const logout = ({ reason } = {}) => {
  axios.post(`${API}/logout`, {}, { withCredentials: true }).catch(() => {});
  if (reason) {
    localStorage.setItem("logoutReason", reason);
  }
  LEGACY_TOKEN_KEYS.forEach((key) => localStorage.removeItem(key));
  localStorage.removeItem(AUTH_USER_KEY);
  localStorage.removeItem(AUTH_EXPIRES_KEY);
  localStorage.removeItem("user_id");
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(AUTH_LOGOUT_EVENT, {
        detail: { reason: reason || "" },
      })
    );
  }
};

export const getTokenExpiryMs = () => {
  const expiresAt = Date.parse(localStorage.getItem(AUTH_EXPIRES_KEY) || "");
  return Number.isFinite(expiresAt) ? expiresAt : null;
};

export const getCurrentUser = () => {
  const rawUser = localStorage.getItem(AUTH_USER_KEY);
  if (!rawUser) return null;
  try {
    const expiresAt = getTokenExpiryMs();
    if (expiresAt && expiresAt < Date.now()) {
      logout({ reason: SESSION_EXPIRED_MESSAGE });
      return null;
    }
    const user = JSON.parse(rawUser);
    user.user_group = Number(user.user_group);
    return user;
  } catch (err) {
    console.error("Session decode error:", err);
    logout();
    return null;
  }
};

export const isAuthenticated = () => !!getCurrentUser();

export const refreshCurrentSession = async () => {
  try {
    const { data } = await axios.get(`${API}/me`, {
      withCredentials: true,
      suppressGlobalLoader: true,
    });
    if (data?.user) {
      storeAuthenticatedSession(data);
      return data.user;
    }
  } catch {
    localStorage.removeItem(AUTH_USER_KEY);
    localStorage.removeItem(AUTH_EXPIRES_KEY);
    localStorage.removeItem("user_id");
  }
  return null;
};

/* ─────────────────────────────────────────────
   Forgot-/reset-password helpers
   ───────────────────────────────────────────── */

export const sendPasswordReset = async (identifier) => {
  const { data } = await axios.post(`${API}/forgot-password`, { identifier });
  return data;
};

export const resetPassword = async (token, password) => {
  const { data } = await axios.post(`${API}/reset-password`, { token, password });
  return data;
};
